import React, { useCallback, useEffect, useRef } from "react";

import { useLocation } from "react-router-dom";

import { useProfile } from "app/context";
import { useDocumentEvent, useDoor } from "app/hooks";
import { containedBy, Trans } from "app/utils";

import { ActionButton, classnames, PlainButton } from "@culturehq/components";

import SocialChannel from "components/common/SocialChannel";
import StoryShareFacebook from "components/facebook/StoryShareFacebook";
import StoryShareInstagram from "components/instagram/StoryShareInstagram";
import StoryShareLinkedin from "components/linkedin/StoryShareLinkedin";

import styles from "./styles.module.scss";
import TiktokShare from "./TiktokShare";

const SocialOption = ({
  approvedForSocial, approvedMessage, children, onCreatedApprovedMessage, socialType, story
}) => {
  if (socialType === "facebook" || socialType === "Facebook") {
    return (
      <StoryShareFacebook approvedForSocial={approvedForSocial} story={story}>
        {children}
      </StoryShareFacebook>
    );
  }

  if (socialType === "instagram" || socialType === "Instagram") {
    return (
      <StoryShareInstagram approvedForSocial={approvedForSocial} story={story}>
        {children}
      </StoryShareInstagram>
    );
  }

  if (socialType === "linkedin" || socialType === "Linkedin") {
    return (
      <StoryShareLinkedin
        approvedForSocial={approvedForSocial}
        onCreate={onCreatedApprovedMessage}
        story={story}
      >
        {children}
      </StoryShareLinkedin>
    );
  }

  if ((socialType === "tiktok" || socialType === "TikTok") && story.media.mediaType === "video") {
    return (
      <TiktokShare approvedMessage={approvedMessage} story={story}>
        {children}
      </TiktokShare>
    );
  }

  if (socialType === "twitter") {
    return (
      <div>
        {children}
      </div>
    );
  }

  return <></>;
};


const ShareOptions = ({ approvedForSocial, onCreatedApprovedMessage, organization, story }) => {
  if (approvedForSocial) {
    return (
      <ul className="dropdown-menu">
        {approvedForSocial.approvedForSocialMessages?.filter(
          (value, index, self) => self.findIndex(
            element => element.socialType === value.socialType
          ) === index
        ).map(approvedMessage => (
          <li key={approvedMessage.id}>
            <SocialOption
              approvedForSocial={approvedForSocial}
              approvedMessage={approvedMessage}
              socialType={approvedMessage.socialType}
              story={story}
            >
              <PlainButton className="SocialOption__Container">
                <SocialChannel socialType={approvedMessage.socialType} useImageIcon />
              </PlainButton>
            </SocialOption>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="dropdown-menu">
      {organization?.organizationSocialMediaTypes?.map(socialMediaType => (
        <li key={socialMediaType.id}>
          <SocialOption
            approvedForSocial={approvedForSocial}
            approvedMessage=""
            onCreatedApprovedMessage={onCreatedApprovedMessage}
            socialType={socialMediaType.socialMediaType}
            story={story}
          >
            <PlainButton className="SocialOption__Container">
              <SocialChannel socialType={socialMediaType.socialMediaType} useImageIcon />
            </PlainButton>
          </SocialOption>
        </li>
      ))}
    </ul>
  );
};

const ShareStoryOptionsButton = ({
  className, children, onClose, open, onToggle, closeOnOutsideClick
}) => {
  const containerRef = useRef();

  useDocumentEvent("click", useCallback(
    event => {
      if (!containedBy(event.target, "chq-mdl") && containerRef.current
        && !containerRef.current.contains(event.target) && closeOnOutsideClick) {
        onClose();
      }
    },
    [closeOnOutsideClick, onClose]
  ));

  const classList = classnames(
    "QuickOptions",
    "QuickOptions--Actions",
    className,
    styles.quick,
    styles[className],
    open && styles["quick-open"]
  );

  return (
    <div ref={containerRef} className={classList}>
      <ActionButton aria-label="Share" icon="share" onClick={onToggle}>
        <Trans>Share</Trans>
      </ActionButton>
      {open && (
        <div className={classnames("QuickOptions__menu-wrapper", styles["quick--wrap"])}>
          <div className={classnames("QuickOptions__menu box-shadow", styles["quick--menu"])}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const SocialShareOptions = ({
  approvedForSocial,
  className,
  closeOnOutsideClick = false,
  onCreatedApprovedMessage,
  story
}) => {
  const location = useLocation();
  const { profile } = useProfile();
  const { organization } = profile;
  const { open, onClose, onToggle } = useDoor(false);

  useEffect(
    () => {
      const paths = location.pathname.split("/");
      if (paths[paths.length - 1] === "share" && `${story.id}` === paths[paths.length - 2]) {
        onToggle();
      }
    },
    [location.pathname, profile, story.id, onToggle]
  );

  if (organization?.organizationSocialMediaTypes.length > 0) {
    return (
      <ShareStoryOptionsButton
        open={open}
        onClose={onClose}
        onToggle={onToggle}
        className={className}
        closeOnOutsideClick={closeOnOutsideClick}
      >
        <ShareOptions
          approvedForSocial={approvedForSocial}
          onClose={onClose}
          onCreatedApprovedMessage={onCreatedApprovedMessage}
          organization={organization}
          story={story}
        />
      </ShareStoryOptionsButton>
    );
  }
  return <></>;
};

export default SocialShareOptions;
